import { ComponentRef, createComponent, EnvironmentInjector, Injectable } from '@angular/core';
import { ConfigService } from '@aiwyn/config';
import { BoxPreviewComponent, DMSPreview, FileModel, ignoreFalsy, Logger } from '@aiwyn/shared';
import { firstValueFrom, of, take, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ScriptLoaderService } from './script-loader.service';
import { DmsIntegrationApiService } from '../shared/components/upload-files/dms-integration-api.service';
import { FilePreviewComponent, FilePreviewData } from '../shared/components/file-preview/file-preview.component';
import { WebViewerPreloadService } from '../shared/services/webviewer';

interface DMSIntegration {
  init: () => void;
  preview: (file: Partial<FileModel>, canDownload?: boolean) => void;
}

@Injectable({
  providedIn: 'root',
})
export class FilePreviewService {
  private readonly _class = 'FilePreviewService';
  private _activeInstance: ComponentRef<DMSPreview> = null;
  private readonly _supportedDMSIntegrations = new Map<string, DMSIntegration>([
    [
      'box',
      {
        init: async () => {
          this._logger.debug(`${this._class} :: _supportedDMSIntegrations :: init :: Loading Box preview script`);
          await firstValueFrom(this._scriptLoader.loadScript('https://cdn01.boxcdn.net/platform/preview/3.6.0/en-US/preview.js', true));
          this._logger.debug(`${this._class} :: _supportedDMSIntegrations :: init :: Box preview script loaded`);
        },
        preview: (file: Partial<FileModel>, canDownload = true) => {
          this._activeInstance = null;
          this._logger.debug(`${this._class} :: _supportedDMSIntegrations :: preview :: Previewing Box file: ${file.id}`);

          this._activeInstance = createComponent(BoxPreviewComponent, {
            environmentInjector: this._environmentInjector,
          });
          this._activeInstance.setInput('file', file);
          this._activeInstance.setInput('showDownload', canDownload);
          document.body.appendChild(this._activeInstance.location.nativeElement);

          this._logger.debug(
            `${this._class} :: _supportedDMSIntegrations :: preview :: Setting up active instance subscriptions for ${this._activeInstance.instance}`,
          );
          this.setUpActiveInstanceSubscriptions(canDownload);

          this._activeInstance.changeDetectorRef.detectChanges();
        },
      },
    ],
  ]);

  constructor(
    private readonly _configService: ConfigService,
    private readonly _dialog: MatDialog,
    private readonly _dmsIntegrationApiService: DmsIntegrationApiService,
    private readonly _logger: Logger,
    private readonly _scriptLoader: ScriptLoaderService,
    private readonly _environmentInjector: EnvironmentInjector,
    private readonly _webViewerPreload: WebViewerPreloadService,
  ) {
    this.initializeDMSPreviewer();
  }

  public preview(file: Partial<FileModel>, canDownload = true): void {
    console.assert(!!file.id, 'File ID is required');

    if (this._supportedDMSIntegrations.has(this._configService.dms)) {
      this._logger.info(`${this._class} :: preview :: Previewing file: ${file.id} with ${this._configService.dms} previewer`);
      this._supportedDMSIntegrations.get(this._configService.dms).preview(file, canDownload);
      return;
    }

    this._logger.info(`${this._class} :: preview :: Previewing file: ${file.id} with Apryse previewer`);
    this.openApryseDialog([file], 0, canDownload);
  }

  /**
   * Multi-file preview. Always uses the Apryse-backed FilePreviewComponent — multi-file is a
   * capability the Box previewer doesn't have, so Box tenants fall back to a single-file preview.
   */
  public previewMany(files: Partial<FileModel>[], initialIndex = 0, canDownload = true): void {
    if (!files?.length) {
      this._logger.warn(`${this._class} :: previewMany :: No files provided for preview`);
      return;
    }

    if (this._supportedDMSIntegrations.has(this._configService.dms)) {
      this._logger.info(`${this._class} :: previewMany :: Previewing ${files.length} file(s) with ${this._configService.dms} previewer`);
      this._supportedDMSIntegrations.get(this._configService.dms).preview(files[this.clampIndex(initialIndex, files.length)], canDownload);
      return;
    }

    this._logger.info(`${this._class} :: previewMany :: Previewing ${files.length} file(s) with Apryse previewer`);
    this.openApryseDialog(files, initialIndex, canDownload);
  }

  /**
   * Previews a document the caller has already resolved a URL for, in the same dialog every other
   * preview uses.
   *
   * The id-keyed paths above resolve their URL through the DMS integration, which needs a File the
   * portal can name. An eSign signed copy is presigned by the signing-context read and reaches the
   * client as a URL and nothing else, so it has no id to key on — but the dialog only ever needs a
   * resolver, so the document is previewable without inventing one.
   *
   * `file.name` still has to carry the extension: the streaming processor decides whether it can
   * handle the document from that alone.
   */
  public previewByUrl(file: Partial<FileModel>, url: string, canDownload = true): void {
    this._webViewerPreload.preload();

    this._dialog.open<FilePreviewComponent, FilePreviewData, void>(FilePreviewComponent, {
      data: {
        files: [file],
        initialIndex: 0,
        canDownload,
        getFileUrl: () => of(url),
      },
      panelClass: 'app-file-preview-dialog',
      backdropClass: 'black-background',
    });
  }

  /** Mirrors FilePreviewComponent's clampInitialIndex — the Box previewer has no downstream clamp of its own. */
  private clampIndex(index: number, length: number): number {
    if (length === 0) return 0;
    return Math.min(Math.max(index, 0), length - 1);
  }

  private openApryseDialog(files: Partial<FileModel>[], initialIndex: number, canDownload: boolean): void {
    // Warm the shared WebViewer instance while the dialog animates in. Idempotent, and a no-op
    // when a signing flow already preloaded it.
    this._webViewerPreload.preload();

    this._dialog.open<FilePreviewComponent, FilePreviewData, void>(FilePreviewComponent, {
      data: {
        files,
        initialIndex,
        canDownload,
        getFileUrl: (fileParam) => this._dmsIntegrationApiService.getDownloadUrl({ files: [fileParam.id], isMultipleDownload: false }),
      },
      panelClass: 'app-file-preview-dialog',
      backdropClass: 'black-background',
    });
  }

  private initializeDMSPreviewer(): void {
    this._configService.configsLoaded$
      .pipe(
        ignoreFalsy(),
        take(1),
        tap((_) => {
          if (this._supportedDMSIntegrations.has(this._configService.dms)) {
            this._logger.info(`${this._class} :: initializeDMSPreviewer :: Initializing DMS previewer for ${this._configService.dms}`);
            this._supportedDMSIntegrations.get(this._configService.dms).init();
            return;
          }

          this._logger.debug(`${this._class} :: initializeDMSPreviewer :: No DMS previewer found for ${this._configService.dms}`);
        }),
      )
      .subscribe();
  }

  private destroyActiveInstance(): void {
    if (this._activeInstance) {
      this._activeInstance.location.nativeElement.remove();
      this._activeInstance.destroy();
      this._activeInstance = null;
    }
  }

  private setUpActiveInstanceSubscriptions(canDownload: boolean): void {
    const closeSubscription = this._activeInstance.instance.close.subscribe(() => {
      this.destroyActiveInstance();
      closeSubscription.unsubscribe();
    });

    const errorSubscription = this._activeInstance.instance.error.subscribe((error) => {
      this._logger.error(`${this._class} :: setUpActiveInstanceSubscriptions :: Error: ${error}`);
      this.openApryseDialog([this._activeInstance.instance.file()], 0, canDownload);

      this.destroyActiveInstance();
      errorSubscription.unsubscribe();
    });
  }
}
